import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, MenuItem, Button, Box,
  Paper, Stack, Card, CardContent, CardActions,
  Checkbox, FormControlLabel, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';
import { validateName, validatePhone, runValidators, PHONE_RULE_KEY } from '../../utils/validation';
import dayjs from 'dayjs';

export default function CreateBooking() {
  const { t, i18n } = useTranslation();
  const { user, workspaceRole, activeWorkspaceId, activeWorkspace } = useAuth();
  // Непідтверджений салон не може створювати записи (бекенд теж відхилить)
  const workspacePending = activeWorkspace && activeWorkspace.status !== 'active';
  const isManager = workspaceRole === 'owner' || workspaceRole === 'admin';
  const [params] = useSearchParams();
  const date = params.get('date');
  const initialTime = params.get('time');
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [userId, setUserId] = useState(null);
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [price, setPrice] = useState(''); // індивідуальна ціна (порожнє = сума послуг)
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showSlots, setShowSlots] = useState(false);
  const [confirmUrl, setConfirmUrl] = useState('');
  const [sendSms, setSendSms] = useState(true);   // авто-надсилання лінка SMS
  const [sendEmail, setSendEmail] = useState(true); // ...та email
  const [clientEmail, setClientEmail] = useState(''); // email існуючого клієнта (для чекбокса Email)
  const [notified, setNotified] = useState(null);  // що реально пішло (з відповіді бекенду)
  const [createdInfo, setCreatedInfo] = useState(null); // короткі деталі створеного запису для модалки
  const [copied, setCopied] = useState(false);

  // Перевірка чи обрана дата/час в минулому (для UI)
  const isPast = (() => {
    if (!date || !initialTime) return false;
    const bookingDateTime = dayjs(`${date}T${initialTime}`);
    return bookingDateTime.isBefore(dayjs());
  })();

  // Майстри салону: менеджер — усіх працівників; виконавець — лише себе.
  useEffect(() => {
    async function fetchMasters() {
      try {
        if (isManager && activeWorkspaceId) {
          const r = await api.getWorkspaceMembers(activeWorkspaceId);
          setMasters((r.members || []).map(m => ({ id: m.userId, name: m.name })));
        } else {
          const me = user || (await getCurrentUser());
          if (!me) { setMasters([]); return; }
          setMasters([{ id: me.id, name: me.name }]);
          setSelectedMaster(me.id);
        }
      } catch (e) {
        setError(t('createBooking.mastersLoadFailed'));
        setMasters([]);
      }
    }
    fetchMasters();
    // eslint-disable-next-line
  }, [isManager, activeWorkspaceId]);

  // Завантажити послуги для обраного майстра
  useEffect(() => {
    async function fetchServices() {
      if (selectedMaster) {
        try {
          const filteredServices = await api.getServices(selectedMaster, activeWorkspaceId);
          setServices(filteredServices);
          setSelected([]);
        } catch {
          setServices([]);
        }
      } else {
        setServices([]);
      }
    }
    fetchServices();
  }, [selectedMaster]);

  // Пошук користувача по телефону для автозаповнення імені
  const handlePhoneCheck = async () => {
    if (!phone) return;
    try {
      const found = await api.getUserByPhone(phone);
      if (found) {
        setName(found.name || '');
        setUserId(found.id);
        setClientEmail(found.email || '');
      } else {
        setName('');
        setUserId(null);
        setClientEmail('');
      }
    } catch {
      setName('');
      setUserId(null);
      setClientEmail('');
    }
  };

  // Знайти доступні таймслоти
  const findAvailableSlots = (bookings, duration, day, selectedTime) => {
    const allSlots = [];
    for (let h = 7; h < 21; h++) {
      for (let m = 0; m < 60; m += 15) {
        const start = dayjs(`${day}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        const end = start.add(duration, 'minute');
        if (end.hour() >= 21 && end.minute() > 0) continue;
        const overlap = bookings.some(b => {
          const bStart = dayjs(b.date).hour(Number(b.time.slice(0,2))).minute(Number(b.time.slice(3,5)));
          const bDuration = Array.isArray(b.services)
            ? b.services.reduce((acc, s) => acc + (s.blockDuration ?? s.duration ?? 0), 0)
            : 0;
          const bEnd = bStart.add(bDuration, 'minute');
          return start.isBefore(bEnd) && bStart.isBefore(end);
        });
        if (!overlap) {
          allSlots.push(start.format('HH:mm'));
        }
      }
    }
    // Вибране користувачем початкове значення
    const idx = allSlots.findIndex(t => t === selectedTime);
    const from = Math.max(0, idx - 5);
    const to = Math.min(allSlots.length, idx + 10);
    return allSlots.slice(from, to);
  };

  // Створити запис
  const handleCreate = async (customTime) => {
    setError('');
    setAvailableSlots([]);
    setShowSlots(false);
    const finalTime = customTime || initialTime;

    // --- Перевірка на минуле (додатково) ---
    const now = dayjs();
    const bookingDateTime = dayjs(`${date}T${finalTime}`);
    if (bookingDateTime.isBefore(now)) {
      setError(t('createBooking.pastError'));
      return;
    }

    const fErrs = runValidators({
      name: validateName(name),
      phone: validatePhone(phone),
    });
    setFieldErrors(fErrs);
    if (Object.keys(fErrs).length > 0) {
      setError(t('createBooking.checkNamePhone'));
      return;
    }
    if (selected.length === 0 || !selectedMaster) {
      setError(t('createBooking.selectMasterService'));
      return;
    }
    setLoading(true);

    try {
      let user = null;
      if (!userId) {
        // Створюємо нового користувача, якщо не знайдений
        const data = await api.createUser({ name, phone, role: 'guest' });
        user = {
          ...data?.user || {},
        };
      } else {
        user = {
          id: userId,
          name,
          phone
        };
      }

      const selectedServices = services.filter(s => selected.includes(s.id));
      // Слід у календарі = час бронювання (block_duration), не час роботи
      const duration = selectedServices.reduce((acc, s) => acc + (s.blockDuration ?? s.duration ?? 0), 0);
      const day = dayjs(date).format('YYYY-MM-DD');
      const allBookings = await api.getBookings();
      const bookingsSameMasterSameDay = allBookings
        .filter(b => b.masterId === Number(selectedMaster) && dayjs(b.date).format('YYYY-MM-DD') === day);

      const newStart = dayjs(`${date}T${finalTime}`);
      const newEnd = newStart.add(duration, 'minute');
      const conflict = bookingsSameMasterSameDay.some(b => {
        const bStart = dayjs(b.date).hour(Number(b.time.slice(0,2))).minute(Number(b.time.slice(3,5)));
        const bDuration = Array.isArray(b.services)
          ? b.services.reduce((acc, s) => acc + (s.blockDuration ?? s.duration ?? 0), 0)
          : 0;
        const bEnd = bStart.add(bDuration, 'minute');
        return newStart.isBefore(bEnd) && bStart.isBefore(newEnd);
      });

      if (conflict) {
        const slots = findAvailableSlots(bookingsSameMasterSameDay, duration, day, initialTime);
        setAvailableSlots(slots);
        setError(t('createBooking.timeTaken'));
        setShowSlots(true);
        setLoading(false);
        return;
      }

      const payload = {
        masterId: Number(selectedMaster),
        userId: user.id,
        workspaceId: activeWorkspaceId,
        date,
        time: finalTime,
        services: selected,
        ...(price !== '' ? { price: Number(price) } : {}),
        sendSms: !!phone.trim() && sendSms,
        sendEmail: !!clientEmail && sendEmail,
      };
      const created = await api.createBooking(payload);
      if (created && created.confirmUrl) {
        setConfirmUrl(created.confirmUrl); // майстер надішле лінк клієнту
        setNotified(created.notified || null);
        setCreatedInfo({
          date,
          time: finalTime,
          services: services.filter(s => selected.includes(s.id)).map(s => pickServiceName(s, i18n.language)),
          clientName: name || '',
          clientPhone: phone,
        });
      } else {
        navigate('/master/dashboard');
      }
    } catch (e) {
      setError(e.message || t('createBooking.createError'));
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleGoBackCalendar = () => {
    navigate('/master/dashboard');
  };

  if (workspacePending) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>{t('createBooking.title')}</Typography>
          <Alert severity="warning">{t('createBooking.workspacePending')}</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#faf7f2', color: '#1a1a16' }}>
        <Typography variant="h6" gutterBottom>{t('createBooking.title')}</Typography>

        <TextField
          label={t('common.phone')}
          fullWidth
          value={phone}
          placeholder="+48601234567"
          onChange={e => { setPhone(e.target.value); setFieldErrors(p => ({ ...p, phone: '' })); }}
          onBlur={handlePhoneCheck}
          error={!!fieldErrors.phone} helperText={t(fieldErrors.phone || PHONE_RULE_KEY)}
          sx={{ mt: 2 }}
        />

        <TextField
          label={t('common.name')}
          fullWidth
          value={name}
          onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
          disabled={!!userId}
          error={!!fieldErrors.name} helperText={fieldErrors.name ? t(fieldErrors.name) : ' '}
          sx={{ mb: 1 }}
        />

        <TextField
          label={t('createBooking.master')}
          select
          fullWidth
          value={selectedMaster}
          onChange={e => setSelectedMaster(e.target.value)}
          sx={{ mb: 2 }}
        >
          {masters.length === 0 && (
            <MenuItem disabled value="">{t('createBooking.noMasters')}</MenuItem>
          )}
          {masters.map(m => (
            <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
          ))}
        </TextField>

        {services.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>{t('createBooking.servicesLabel')}</Typography>
            <Stack spacing={2} mb={2}>
              {services.map(s => (
                <Card
                  key={s.id}
                  sx={{
                    backgroundColor: selected.includes(s.id) ? '#6c1129' : '#f1eae0',
                    color: '#1a1a16',
                    borderRadius: 2,
                    boxShadow: selected.includes(s.id) ? 6 : 2
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">{pickServiceName(s, i18n.language)}</Typography>
                    <Typography variant="body2">{t('createBooking.duration', { duration: s.duration })}</Typography>
                    <Typography variant="body2">{t('createBooking.price', { price: s.price ? formatMoney(s.price) : '—' })}</Typography>
                  </CardContent>
                  <CardActions>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected.includes(s.id)}
                          onChange={() => setSelected(prev =>
                            prev.includes(s.id)
                              ? prev.filter(i => i !== s.id)
                              : [...prev, s.id])}
                          sx={{ color: '#1a1a16' }}
                        />
                      }
                      label={t('createBooking.choose')}
                    />
                  </CardActions>
                </Card>
              ))}
            </Stack>
          </>
        )}

        {/* Індивідуальна ціна запису (порожнє = сума послуг) */}
        {selected.length > 0 && (
          <TextField
            label={t('createBooking.priceLabel')}
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))}
            size="small"
            type="number"
            placeholder={String(
              services.filter((s) => selected.includes(s.id)).reduce((a, s) => a + Number(s.price || 0), 0)
            )}
            helperText={t('createBooking.priceHint')}
            sx={{ mb: 2, width: 240 }}
            inputProps={{ min: 0, step: '0.01' }}
            InputLabelProps={{ shrink: true }}
          />
        )}

        {/* Блокування створення якщо дата/час у минулому */}
        {isPast && (
          <Typography color="error" sx={{ mb: 1 }}>
            {t('createBooking.pastError')}
          </Typography>
        )}

        {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}

        {showSlots && availableSlots.length > 0 && (
          <Stack spacing={1} sx={{ my: 2 }}>
            <Typography>{t('createBooking.nearestSlots')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {availableSlots.map(t => (
                <Button
                  key={t}
                  variant={t === initialTime ? 'contained' : 'outlined'}
                  size="small"
                  sx={{ mb: 1 }}
                  onClick={() => handleCreate(t)}
                >
                  {t}
                </Button>
              ))}
            </Stack>
            <Button variant="text" onClick={handleGoBackCalendar} sx={{ mt: 1, color: '#1a1a16', borderColor: '#1a1a16' }}>
              {t('createBooking.backToCalendar')}
            </Button>
          </Stack>
        )}

        {!confirmUrl && (phone.trim() || clientEmail) && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{t('createBooking.autoSendLabel')}</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {phone.trim() && (
                <FormControlLabel
                  control={<Checkbox checked={sendSms} onChange={e => setSendSms(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                  label={t('createBooking.sendSms')}
                />
              )}
              {clientEmail && (
                <FormControlLabel
                  control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                  label={t('createBooking.sendEmail')}
                />
              )}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" color="inherit" fullWidth onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCreate()}
            fullWidth
            disabled={loading || isPast || !!confirmUrl}
          >
            {loading ? t('createBooking.creating') : t('createBooking.title')}
          </Button>
        </Stack>
      </Paper>

      {/* Результат створення — модалка (форма позаду інертна, дублі неможливі) */}
      <Dialog open={!!confirmUrl} onClose={() => navigate('/master/dashboard')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>✅ {t('createBooking.createdTitle')}</DialogTitle>
        <DialogContent>
          {createdInfo && (
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('bookingDetails.client')}:</strong> {createdInfo.clientName || createdInfo.clientPhone}
              </Typography>
              <Typography variant="body2">
                <strong>{t('bookingDetails.date')}:</strong> {createdInfo.date} · {createdInfo.time}
              </Typography>
              {createdInfo.services.length > 0 && (
                <Typography variant="body2">
                  <strong>{t('bookingDetails.services')}:</strong> {createdInfo.services.join(', ')}
                </Typography>
              )}
            </Stack>
          )}
          <DialogContentText sx={{ mb: 1, color: 'text.primary' }}>
            {t('createBooking.createdSendLink')}
          </DialogContentText>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField value={confirmUrl} size="small" fullWidth InputProps={{ readOnly: true }} />
            <Button
              variant="outlined"
              onClick={() => { navigator.clipboard.writeText(confirmUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            >
              {copied ? t('common.copied') : t('common.copy')}
            </Button>
          </Stack>
          {notified && (notified.sms || notified.email) && (
            <Stack spacing={0.3} sx={{ mt: 1 }}>
              {notified.sms && <Typography variant="caption">📲 {t('createBooking.sentSms')}</Typography>}
              {notified.email && <Typography variant="caption">📧 {t('createBooking.sentEmail')}</Typography>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/master/dashboard')}
            sx={{ bgcolor: '#6c1129', color: '#fff', fontWeight: 600, textTransform: 'none',
                  boxShadow: 'none', '&:hover': { bgcolor: '#977536' } }}
          >
            {t('createBooking.toCalendar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
